import type { ApplianceId } from "~domain/devices/models/appliances/appliance-model.ts";
import type { Appliance } from "~domain/devices/models/appliances/appliance-model.ts";
import type { ModulatorLink } from "~domain/devices/models/links/link-model.ts";
import type { ModulatorConsumption } from "~domain/devices/models/modulators/modulator-consumption.ts";
import type { ModulatorLastPoint } from "~domain/devices/models/modulators/modulator-last-point-model.ts";
import type { ModulatorLastTemperaturePoint } from "~domain/devices/models/modulators/modulator-last-temperature-point-model.ts";
import type { Modulator, ModulatorCsId } from "~domain/devices/models/modulators/modulator-model.ts";
import type { BackofficeFindAppliancesResponse } from "~infrastructure/api/backoffice/contract/devices/responses/backoffice-find-appliances-response.ts";
import type { BackofficeFindLinksResponse } from "~infrastructure/api/backoffice/contract/devices/responses/backoffice-find-links-response.ts";
import type { BackofficeFindModulatorLastTemperaturePointResponse } from "~infrastructure/api/backoffice/contract/devices/responses/backoffice-find-modulator-last-temperature-point-response.ts";
import type { BackofficeFindModulatorResponse } from "~infrastructure/api/backoffice/contract/devices/responses/backoffice-find-modulator-response.ts";
import type { BackofficeLastPointResponse } from "~infrastructure/api/backoffice/contract/devices/responses/backoffice-last-point-response.ts";
import type { BackofficeModulatorConsumptionResponse } from "~infrastructure/api/backoffice/contract/devices/responses/backoffice-modulator-consumption-response.ts";
import type { BackofficeFindSiteModulatorsResponse } from "~infrastructure/api/backoffice/contract/site/responses/backoffice-find-site-modulators-response.ts";

import { EModulatorType } from "~domain/devices/models/modulators/modulator-type-model.ts";

export const ModulatorMapper = {
  fromBackofficeLinkApplianceToModel(
    rawLink: BackofficeFindLinksResponse[number],
    rawAppliances: BackofficeFindAppliancesResponse,
  ): ModulatorLink {
    return {
      csId: rawLink.csId,
      hwId: rawLink.hwId,
      measureIndex: rawLink.measureIndex,
      name: rawLink.interfaceName ?? undefined,
      appliances: rawAppliances.map((rawAppliance) => ({
        id: rawAppliance.id ?? undefined,
        linkCsId: rawAppliance.csLinkId,
        linkHwId: rawAppliance.hwLinkId,
        localisation: rawAppliance.localisation ?? undefined,
        type: {
          description: rawAppliance.appliance.description,
          id: rawAppliance.appliance.id,
        },
      })),
    };
  },

  fromBackofficeModulatorConsumptionsToDomainConsumptions(
    modulatorCsId: ModulatorCsId,
    backofficeConsumption: Array<BackofficeModulatorConsumptionResponse>,
  ): ModulatorConsumption | undefined {
    // when backoffice returns void, it's equivalent of an empty blob
    if (!backofficeConsumption || backofficeConsumption instanceof Blob) {
      return undefined;
    }

    return {
      csId: modulatorCsId,
      points: backofficeConsumption.map((bc) => ({
        date: bc.dateMeter,
        powerValue1: bc.powerValue1 ?? undefined,
        powerValue2: bc.powerValue2 ?? undefined,
        powerValue3: bc.powerValue3 ?? undefined,
        powerValue4: bc.powerValue4 ?? undefined,
        powerValue5: bc.powerValue5 ?? undefined,
        powerValue6: bc.powerValue6 ?? undefined,
        value:
          bc.powerValue1 ??
          bc.powerValue2 ??
          bc.powerValue3 ??
          bc.powerValue4 ??
          bc.powerValue5 ??
          bc.powerValue6 ??
          undefined,
      })),
    };
  },

  fromBackofficeLastPointToDomainLastPoint(
    modulatorCsId: ModulatorCsId,
    // biome-ignore lint/suspicious/noConfusingVoidType: it really can be void
    backofficeLastPoint: BackofficeLastPointResponse | void,
  ): ModulatorLastPoint | undefined {
    // when backoffice returns void, it's equivalent of an empty blob
    if (!backofficeLastPoint || backofficeLastPoint instanceof Blob) {
      return undefined;
    }

    return {
      csId: modulatorCsId,
      date: backofficeLastPoint.dateMeter ?? undefined,
      powerValue1: backofficeLastPoint.powerValue1 ?? undefined,
      powerValue2: backofficeLastPoint.powerValue2 ?? undefined,
      powerValue3: backofficeLastPoint.powerValue3 ?? undefined,
      powerValue4: backofficeLastPoint.powerValue4 ?? undefined,
      powerValue5: backofficeLastPoint.powerValue5 ?? undefined,
      powerValue6: backofficeLastPoint.powerValue6 ?? undefined,
    };
  },

  fromBackofficeModulatorToDomainModulator(
    // biome-ignore lint/suspicious/noConfusingVoidType: it really can be void
    backofficeModulator: BackofficeFindModulatorResponse | void,
  ): Modulator | undefined {
    // when backoffice returns void, it's equivalent of an empty blob
    if (!backofficeModulator || backofficeModulator instanceof Blob) {
      return undefined;
    }

    return {
      csId: backofficeModulator.csId ?? undefined,
      hwId: backofficeModulator.hwId,
      modnum: backofficeModulator.modnum ?? undefined,
      localisation: backofficeModulator.location ?? undefined,
      neuronId: backofficeModulator.neuronId,
      creationDatetime: backofficeModulator.creationDatetime,
      pilot: backofficeModulator.momboxId
        ? {
            csId: backofficeModulator.momboxCsId ?? undefined,
            hwId: backofficeModulator.momboxId,
          }
        : undefined,
      serialNumber: backofficeModulator.serialNumber ?? undefined,
      siteId: backofficeModulator.siteId ?? undefined,
      type: {
        kind: backofficeModulator.vx ? EModulatorType.VX : EModulatorType.V2V5,
        id: backofficeModulator.type.id,
        note: backofficeModulator.type.note,
      },
      links: [],
    };
  },

  mapBackofficeSiteModulatorsToDomain(
    backofficeSiteModulators: BackofficeFindSiteModulatorsResponse,
  ): Array<Modulator> {
    return backofficeSiteModulators.map(
      (mod) =>
        ({
          hwId: mod.hwId,
          csId: mod.csId ?? undefined,
          serialNumber: mod.serialNumber ?? undefined,
          neuronId: mod.neuronId,
          modnum: mod.modnum ?? undefined,
          localisation: mod.location ?? undefined,
          creationDatetime: mod.creationDatetime,
          pilot: mod.momboxId
            ? {
                hwId: mod.momboxId,
                csId: mod.momboxCsId ?? undefined,
              }
            : undefined,
          siteId: mod.siteId,
          type: {
            kind: mod.type.vx ? EModulatorType.VX : EModulatorType.V2V5,
            note: mod.type.note,
            id: mod.type.id,
          },
          links: mod.links.map(
            (link) =>
              ({
                csId: link.csId,
                hwId: link.hwId,
                name: link.interfaceName ?? undefined,
                measureIndex: link.measureIndex,
                appliances: link.appliances.map(
                  (appliance) =>
                    ({
                      id: (appliance.id as ApplianceId | null) ?? undefined,
                      type: {
                        id: appliance.appliance.id,
                        description: appliance.appliance.description,
                      },
                      linkCsId: appliance.csLinkId,
                      linkHwId: appliance.hwLinkId,
                      localisation: appliance.localisation ?? undefined,
                    }) satisfies Appliance,
                ),
              }) satisfies ModulatorLink,
          ),
        }) satisfies Modulator,
    );
  },
  fromBackofficeLastTemperaturePointsToDomain(
    backofficeLastTemperaturePoints: BackofficeFindModulatorLastTemperaturePointResponse,
  ): Array<ModulatorLastTemperaturePoint> {
    return backofficeLastTemperaturePoints.map(ModulatorMapper.fromBackofficeLastTemperaturePointToDomain);
  },
  fromBackofficeLastTemperaturePointToDomain(
    backofficeLastTemperaturePoint: BackofficeFindModulatorLastTemperaturePointResponse[number],
  ): ModulatorLastTemperaturePoint {
    return {
      csId: backofficeLastTemperaturePoint.idCsModulator,
      date: backofficeLastTemperaturePoint.dateMeter,
    };
  },
};
